import { GameEngine } from "~/engine/instance";
import { GrenadeEntity } from "~/entities/grenade";
import type { Vector2 } from "~/state/game-types";
import { MiningSystem } from "~/systems/mining-system";
import type { Voxel } from "~/terrain/voxel-terrain";

export class GrenadeSystem {
  private grenades: GrenadeEntity[] = [];

  private static readonly GRAVITY_Y = 800.0; // units/s^2 (Y up)
  private static readonly AIR_DRAG = 0.1; // linear drag per second
  private static readonly GROUND_FRICTION = 4.0; // slows X when grounded
  private static readonly LAUNCH_SPEED = 300.0;

  // visual
  private static readonly FLASH_BASE_HZ = 2.0;
  private static readonly FLASH_HZ_GROWTH = 0.0025; // additional Hz per ms

  // timings
  private static readonly FUSE_MS = 3000; // explode after 3s
  private static readonly EXPLOSION_MS = 500; // digging window while radius expands

  // explosion power
  private static readonly EXPLOSION_MAX_RADIUS_VOXELS = 12; // grows to 12 voxels
  private static readonly EXPLOSION_STRENGTH = 10; // strong digging

  public spawnGrenade(origin: Vector2, angle: number): void {
    const grenade = new GrenadeEntity(origin);

    // Initial velocity
    const launchSpeed = GrenadeSystem.LAUNCH_SPEED;
    grenade.velocity.x = Math.cos(angle) * launchSpeed;
    grenade.velocity.y = Math.sin(angle) * launchSpeed;

    this.grenades.push(grenade);
  }

  public update(deltaTime: number): void {
    // Update in reverse for safe removals
    for (let i = this.grenades.length - 1; i >= 0; i--) {
      const g = this.grenades[i]!;
      const dt = deltaTime;

      // If exploding, run expanding dig then remove when done
      if (g.isExploding) {
        const elapsed = g.explosionElapsedMs;
        if (elapsed >= GrenadeSystem.EXPLOSION_MS) {
          g.dispose();
          this.grenades.splice(i, 1);
          continue;
        }
        this.applyExplosionDig(g, elapsed, dt);
        // keep flashing during explosion
        this.updateFlashing(g);
        continue;
      }

      // Visual flashing gets faster as fuse progresses
      this.updateFlashing(g);

      // Physics: gravity + drag
      g.velocity.y -= GrenadeSystem.GRAVITY_Y * dt;
      g.velocity.x -= g.velocity.x * GrenadeSystem.AIR_DRAG * dt;
      g.velocity.y -= g.velocity.y * GrenadeSystem.AIR_DRAG * dt;

      // Integrate tentative position
      let nextX = g.position.x + g.velocity.x * dt;
      let nextY = g.position.y + g.velocity.y * dt;

      // Prevent embedding deeply in solids horizontally: slide along if needed
      // We only resolve to keep the AABB outside solids, but do not zero X velocity
      const correctedX = this.resolveHorizontalNoStop(g, nextX, g.position.y);
      nextX = correctedX;

      // Ground check: stop only when solid voxel directly below
      const groundedInfo = this.checkGroundedBelow(g, nextX, nextY);
      if (groundedInfo.grounded) {
        nextY = groundedInfo.snapY;
        // stop vertical motion, apply ground friction
        g.velocity.y = 0;
        g.velocity.x -= g.velocity.x * GrenadeSystem.GROUND_FRICTION * dt;
      }

      g.setPosition(nextX, nextY);

      // Fuse trigger
      if (g.ageMs >= GrenadeSystem.FUSE_MS) {
        g.markExploded();
      }
    }
  }

  private updateFlashing(g: GrenadeEntity): void {
    const t = g.ageMs;
    const hz = GrenadeSystem.FLASH_BASE_HZ + t * GrenadeSystem.FLASH_HZ_GROWTH;
    const phase = Math.sin((t / 1000) * Math.PI * 2 * hz) * 0.5 + 0.5; // 0..1
    // Lerp between red and white
    const r = 1.0;
    const gCol = 1.0 * phase + 0.0 * (1 - phase);
    const b = 1.0 * phase + 0.0 * (1 - phase);
    const color =
      (Math.floor(r * 255) << 16) |
      (Math.floor(gCol * 255) << 8) |
      Math.floor(b * 255);
    g.setColor(color);
  }

  private resolveHorizontalNoStop(
    g: GrenadeEntity,
    targetX: number,
    y: number,
  ): number {
    const half = g.size * 0.5;
    // Quick acceptance
    if (
      !this.aabbIntersectsSolid(
        targetX - half,
        y - half,
        targetX + half,
        y + half,
      )
    ) {
      return targetX;
    }
    // Binary search between current and target to stay outside solids
    let low = g.position.x;
    let high = targetX;
    if (low > high) [low, high] = [high, low];
    const eps = 0.001;
    let best = g.position.x;
    for (let iter = 0; iter < 16; iter++) {
      const mid = (low + high) / 2;
      const minX = mid - half;
      const maxX = mid + half;
      const minY = y - half;
      const maxY = y + half;
      if (!this.aabbIntersectsSolid(minX, minY, maxX, maxY)) {
        best = mid;
        low = mid + eps;
      } else {
        high = mid - eps;
      }
      if (Math.abs(high - low) < eps) break;
    }
    return best;
  }

  private checkGroundedBelow(
    g: GrenadeEntity,
    x: number,
    y: number,
  ): { grounded: boolean; snapY: number } {
    const vs = GameEngine.voxelTerrain.voxelSize;
    const half = g.size * 0.5;
    const minX = x - half;
    const maxX = x + half;
    const minY = y - half - 1; // a little below
    const maxY = y - half;
    for (const v of GameEngine.voxelTerrain.getVoxelsInRect(
      { x: minX, y: minY },
      { x: maxX, y: maxY },
      true,
    )) {
      // Snap grenade just on top of this voxel
      const topOfVoxel = v.voxelPos.y * vs + vs + half;
      return { grounded: true, snapY: topOfVoxel };
    }
    return { grounded: false, snapY: y };
  }

  private aabbIntersectsSolid(
    minX: number,
    minY: number,
    maxX: number,
    maxY: number,
  ): boolean {
    // Any solid voxel overlapping area indicates collision
    const it = GameEngine.voxelTerrain.getVoxelsInRect(
      { x: minX, y: minY },
      { x: maxX, y: maxY },
      true,
    );
    for (const _ of it) return true;
    return false;
  }

  private applyExplosionDig(
    g: GrenadeEntity,
    elapsedMs: number,
    deltaTime: number,
  ): void {
    const vs = GameEngine.voxelTerrain.voxelSize;
    const maxRadius = GrenadeSystem.EXPLOSION_MAX_RADIUS_VOXELS * vs;
    const radius = Math.max(
      0.5 * vs,
      (elapsedMs / GrenadeSystem.EXPLOSION_MS) * maxRadius,
    );
    // Visual ring grows and fades
    const opacity = Math.max(0, 1 - elapsedMs / GrenadeSystem.EXPLOSION_MS);
    g.setExplosionRing(radius, opacity);
    const voxels: Voxel[] = [];
    for (const v of GameEngine.voxelTerrain.getVoxelsInRadius(
      g.position,
      radius,
    )) {
      if (v.terrainInfo.resistance != null) voxels.push(v);
    }
    if (voxels.length > 0) {
      MiningSystem.applyDrilling(
        deltaTime,
        voxels,
        GrenadeSystem.EXPLOSION_STRENGTH,
      );
      // Show digging particles
      g.drillEffect.display(voxels);
    } else {
      g.drillEffect.display(null);
    }
  }

  public dispose(): void {
    for (const g of this.grenades) g.dispose();
    this.grenades.length = 0;
  }
}
